import { useMutation } from 'react-query';
import { useGlobals } from '../../globals-context';
import {
  DisablePaypalIntegrationResponse,
  FetchPaypalOnboardingLinkResponse,
  FetchShorePayOnlineOnboardingLinkResponse,
} from '../types';
import {
  disablePaypalIntegration,
  fetchPaypalOnboardingLink,
  fetchShorePayOnlineOnboardingLink,
} from '../api/payments';
import { publishUIMessage } from '../utils/notifications';
import { t } from '../../utils/i18n';

export const paymentErrors = {
  vatIdOrTaxNumberRequired: 'VAT ID or Tax Number is required',
};

export function useDisablePayPalIntegration() {
  const { apiClient } = useGlobals() as any;
  return useMutation({
    mutationKey: ['disable-paypal-integration'],
    mutationFn: ({ merchantId }: { merchantId: string }) =>
      disablePaypalIntegration({ apiClient, merchantId }),
    onSuccess: (data: DisablePaypalIntegrationResponse) => {
      setTimeout(() => window.location.reload(), 2000);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        console.error('Error disabling PayPal integration:');
        publishUIMessage('error', error.message);
      }
    },
  });
}

export function useInitiatePayPalSetup() {
  const { apiClient } = useGlobals() as any;
  return useMutation({
    mutationKey: ['initiate-paypal-setup'],
    mutationFn: ({ merchantId }: { merchantId: string }) =>
      fetchPaypalOnboardingLink({ apiClient, merchantId }),
    onSuccess: (data: FetchPaypalOnboardingLinkResponse) => {
      const paypalSetupURL = data?.links?.action_url;
      if (paypalSetupURL) {
        setTimeout(() => {
          window.open(paypalSetupURL, '_blank')?.focus();
        }, 0);
      }
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        console.error('Error fetching PayPal signup link:');
        publishUIMessage('error', error.message);
      }
    },
  });
}

export function useInitiateShorePayOnlineAccountSetup() {
  const { apiClient } = useGlobals() as any;
  return useMutation({
    mutationKey: ['initiate-shore-pay-online-setup'],
    mutationFn: ({
      taxId,
      merchantId,
      company,
    }: {
      taxId?: string;
      merchantId: string;
      company: string;
    }) =>
      fetchShorePayOnlineOnboardingLink({
        apiClient,
        company,
        taxId,
        merchantId,
      }),
    onSuccess: (result: FetchShorePayOnlineOnboardingLinkResponse) => {
      const { data: shorePayOnlineData, errors: shorePayOnlineErrors } = result;
      if (shorePayOnlineErrors && shorePayOnlineErrors.length > 0) {
        const [error] = shorePayOnlineErrors;
        const { title } = error || {};
        if (title) {
          throw new Error(title);
          return;
        }
      } else if (shorePayOnlineData) {
        const { attributes: { url: shorePaySetupURL } = {} } =
          shorePayOnlineData || {};

        if (shorePaySetupURL) {
          // Safari doesn't support window.open() inside async calls for security reasons
          // therefore, using wrapping it with a setTimeout to run it outside the async call
          setTimeout(() => {
            window.open(shorePaySetupURL, '_blank')?.focus();
          }, 0);
          return;
        }
      }

      throw new Error('ShorePay account setup error');
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        if (!error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)) {
          publishUIMessage(
            'error',
            t('notifications.general.customer-support') || ''
          );
        }
        console.error('ShorePay account setup error');
      }
    },
  });
}
